import { MenuWrapper, useMenuContext } from './MenuWrapper';
// Support → /live-chat (`menu.liveChat` in locales).
import { NavItemButton } from '@/components/ui/navigation/NavItemButton';
import { useInviteArtistDialog } from '@/contexts/InviteArtistContext';
import {
  getMoreMenuItemsForUser,
  getMoreMenuPaths,
  type BadgeState,
  type NavItem,
} from '@/lib/navigationItems';
import { isNative } from '@/lib/platform';
import { useNotificationSettingsListener } from '@/hooks/useNotificationSettingsListener';
import { useLocation } from 'react-router-dom';
import { isCurrentUserAdmin } from '@/hooks/useAdminMode';
import { useAuthState } from '@/hooks/useAuth';
import { isAdminUserEmail } from '@/components/admin/AdminAuthGuard';

const UTILITY_MORE_MENU_IDS = new Set(['liveChat', 'settings']);
const UTILITY_MORE_MENU_ORDER = ['liveChat', 'settings'] as const;

function sortUtilityMenuItems(items: NavItem[]): NavItem[] {
  return [...items].sort(
    (a, b) =>
      UTILITY_MORE_MENU_ORDER.indexOf(a.id as (typeof UTILITY_MORE_MENU_ORDER)[number]) -
      UTILITY_MORE_MENU_ORDER.indexOf(b.id as (typeof UTILITY_MORE_MENU_ORDER)[number]),
  );
}

function ArtistsMenuSection({
  items,
  variant = 'primary',
}: {
  items: NavItem[];
  variant?: 'primary' | 'utility';
}) {
  const { closeMenu } = useMenuContext();
  const { openInviteArtistDialog } = useInviteArtistDialog();
  const { needsSetup: settingsNeedsSetup } = useNotificationSettingsListener();
  const location = useLocation();
  const isAdmin = isCurrentUserAdmin();

  const badgeState: BadgeState = {
    needsNotificationSetup: settingsNeedsSetup,
    isNative: isNative(),
    currentPath: location.pathname,
  };

  return (
    <>
      {items.map((item) => {
        let itemToRender = item;
        if (item.id === 'liveChat' && isAdmin) {
          itemToRender = {
            ...item,
            path: '/live-chat-admin',
            pathMatches: ['/live-chat-admin'],
          } as unknown as typeof item;
        }

        return (
          <div key={item.id} className={variant === 'utility' ? 'contents' : undefined}>
            <NavItemButton
              item={itemToRender}
              badgeState={badgeState}
              variant={variant === 'utility' ? 'utility' : 'default'}
              onNavigate={
                item.id === 'inviteArtist'
                  ? () => {
                      closeMenu();
                      setTimeout(() => openInviteArtistDialog(), 0);
                    }
                  : closeMenu
              }
            />
          </div>
        );
      })}
    </>
  );
}

export function ArtistsMenu() {
  const { needsSetup: settingsNeedsSetup } = useNotificationSettingsListener();
  const { user } = useAuthState();
  const isAdmin = isAdminUserEmail(user?.email);
  const visible = getMoreMenuItemsForUser({ isArtist: true, isAdmin });
  const menuPaths = getMoreMenuPaths(visible);
  const utilityItems = sortUtilityMenuItems(
    visible.filter((item) => UTILITY_MORE_MENU_IDS.has(item.id)),
  );
  const bodyItems = visible.filter((item) => !UTILITY_MORE_MENU_IDS.has(item.id));

  return (
    <MenuWrapper
      needsNotificationSetup={settingsNeedsSetup}
      menuPaths={menuPaths}
      utilityItems={
        utilityItems.length > 0 ? <ArtistsMenuSection items={utilityItems} variant="utility" /> : null
      }
    >
      <ArtistsMenuSection items={bodyItems} />
    </MenuWrapper>
  );
}
